import { useMemo, useCallback, useRef, useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import type { NavigationItem } from "./navigation";
import { cn } from "@/shared/lib/cn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/shared/ui/hover-card";
import { NavigationItemContent } from "./NavigationItemContent";
import {
  convertSearchParams,
  navigateToItem,
  withTooltip,
} from "./navigationUtils";

interface NavigationItemWithSubmenuProps {
  item: NavigationItem;
  collapsed: boolean;
  onNavigate?: () => void;
  iconNode: React.ReactNode;
  isActive: boolean;
}

const HOVER_DELAY = 150;
const HOVER_CARD_OPEN_DELAY = 200;
const HOVER_CARD_CLOSE_DELAY = 100;
const ADMIN_ROUTE = "/admin";

export function NavigationItemWithSubmenu({
  item,
  collapsed,
  onNavigate,
  iconNode,
  isActive,
}: NavigationItemWithSubmenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  const isAdminRoute = useMemo(
    () => item.target.to === ADMIN_ROUTE,
    [item.target.to]
  );

  const linkSearchParams = useMemo(
    () => convertSearchParams(item.target.search),
    [item.target.search]
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (collapsed) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsOpen(true);
  }, [collapsed]);

  const handleMouseLeave = useCallback(() => {
    if (collapsed) return;

    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, HOVER_DELAY);
  }, [collapsed]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (collapsed) {
        onNavigate?.();
        return;
      }

      if (isAdminRoute) {
        e.preventDefault();
        navigateToItem(item, navigate, onNavigate);
      } else {
        onNavigate?.();
      }
    },
    [collapsed, isAdminRoute, item, navigate, onNavigate]
  );

  const handleChildClick = useCallback(
    (childItem: NavigationItem) => {
      setIsOpen(false);
      const childIsAdminRoute = childItem.target.to === ADMIN_ROUTE;

      if (childIsAdminRoute) {
        navigateToItem(childItem, navigate, onNavigate);
      } else {
        onNavigate?.();
      }
    },
    [navigate, onNavigate]
  );

  const childItems = useMemo(
    () =>
      item.children?.map((child) => ({
        ...child,
        searchParams: convertSearchParams(child.target.search),
      })) ?? [],
    [item.children]
  );

  const linkClassName = useMemo(
    () =>
      cn(
        "group relative flex items-center gap-3 overflow-hidden rounded-2xl px-3 py-2 text-sm transition-all duration-200",
        "border border-transparent bg-white/5 font-medium text-white/80",
        "hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/10",
        "focus:outline-none focus-visible:outline-none",
        isActive &&
          "border-imperial-500/70 bg-imperial-500/20 text-white shadow-telemetry",
        "data-[status=active]:border-imperial-500/70 data-[status=active]:bg-imperial-500/20 data-[status=active]:text-white data-[status=active]:shadow-telemetry",
        collapsed && "justify-center px-2"
      ),
    [collapsed, isActive]
  );

  const parentLink = useMemo(
    () => (
      <Link
        to={item.target.to}
        params={item.target.params}
        search={linkSearchParams}
        onClick={handleClick}
        {...(isActive && { "data-status": "active" as const })}
        className={linkClassName}
      >
        <NavigationItemContent
          collapsed={collapsed}
          item={item}
          iconNode={iconNode}
        />
        {!collapsed && (
          <ChevronRight className="ml-auto h-4 w-4 text-white/40" />
        )}
        <span className="absolute inset-y-0 left-0 w-[3px] rounded-r-full bg-linear-to-b from-imperial-400 via-imperial-500 to-imperial-600 opacity-0 transition-opacity duration-200 group-data-[status=active]:opacity-100" />
      </Link>
    ),
    [
      item,
      linkSearchParams,
      handleClick,
      isActive,
      linkClassName,
      collapsed,
      iconNode,
    ]
  );

  const renderHoverCardChildLink = useCallback(
    (
      child: NavigationItem & {
        searchParams: Record<string, string | undefined>;
      }
    ) => (
      <Link
        key={child.id}
        to={child.target.to}
        params={child.target.params}
        search={child.searchParams}
        onClick={() => {
          handleChildClick(child);
          onNavigate?.();
        }}
        className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-white/80 transition-colors hover:bg-white/10 hover:text-white"
      >
        {child.label}
      </Link>
    ),
    [handleChildClick, onNavigate]
  );

  if (collapsed) {
    const hoverCardContent = (
      <HoverCard
        openDelay={HOVER_CARD_OPEN_DELAY}
        closeDelay={HOVER_CARD_CLOSE_DELAY}
      >
        <HoverCardTrigger asChild>{parentLink}</HoverCardTrigger>
        <HoverCardContent side="right" className="w-64 p-2">
          <div className="space-y-1">
            {childItems.map(renderHoverCardChildLink)}
          </div>
        </HoverCardContent>
      </HoverCard>
    );

    return withTooltip(collapsed, item, hoverCardContent);
  }

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative"
    >
      <DropdownMenu
        key={item.id}
        open={isOpen}
        onOpenChange={setIsOpen}
        modal={false}
      >
        <DropdownMenuTrigger asChild>{parentLink}</DropdownMenuTrigger>
        <DropdownMenuContent
          side="right"
          align="start"
          className="w-56 bg-white/95 backdrop-blur border-white/20"
          sideOffset={4}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {childItems.map((child) => (
            <DropdownMenuItem
              key={child.id}
              onClick={() => handleChildClick(child)}
              className="cursor-pointer"
            >
              {child.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
